import { useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Badge } from 'components/common';
import { ProfileCompetenciesDetails } from '.';

import type { CompetencyLevel } from 'shared-types';
import type { BadgeCategory } from 'components/common/Badge';
import type { IconProp } from 'components/common/icons/Icon';
import type { MergedCompetency } from 'lib/competencies/getMergedCompetencies';

interface Props {
  badgeCategory?: BadgeCategory;
  badgeIcon?: IconProp;
  competency: MergedCompetency;
  displayLevel?: Pick<CompetencyLevel, 'name' | 'nameLocales'>;
  enableAdminLinks?: boolean;
  showEndorsements?: boolean;
  showEndorsementsIcon?: boolean;
  showLanguageVersionGrantorTitle?: boolean;
  targetLevelNumber?: number;
  targetUserId?: number;
  updateCompetencies?: () => Promise<void>;
}

const ProfileCompetenciesBadge = ({
  badgeCategory = 'success-light',
  badgeIcon = 'medal',
  competency,
  displayLevel,
  enableAdminLinks,
  showEndorsements,
  showEndorsementsIcon,
  showLanguageVersionGrantorTitle,
  targetLevelNumber,
  targetUserId,
  updateCompetencies
}: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(undefined);

  const { language, messages } = useReduxState();

  const [modalVisible, setModalVisible] = useState(false);

  const hideModal = () => {
    setModalVisible(false);
    setTimeout(() => {
      buttonRef.current?.focus();
    });
  };

  const showModal = () => setModalVisible(true);

  const endorsements = competency.endorsements?.length
    ? competency.endorsements
    : competency.levels
    ? competency.levels.flatMap((level) => level.endorsements)
    : null;

  return (
    <>
      <button className="button" ref={buttonRef} type="button">
        <Badge
          category={badgeCategory}
          icon={badgeIcon}
          iconCircleProgress={
            targetLevelNumber
              ? competency.highestLevel?.level / targetLevelNumber
              : undefined
          }
          isGroupedFirst={Boolean(showEndorsementsIcon && endorsements?.length)}
          isRounded={false}
          onClick={showModal}
          style={{ width: '100%' }}
          text={
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {competency.nameLocales?.[language] || competency.name}
              {displayLevel || competency.highestLevel ? (
                <span>
                  {': '}
                  <span style={{ fontWeight: 400 }}>
                    {(displayLevel || competency.highestLevel).nameLocales?.[
                      language
                    ] || (displayLevel || competency.highestLevel).name}
                  </span>
                </span>
              ) : null}
            </span>
          }
        />

        {showEndorsementsIcon && endorsements?.length ? (
          <Badge
            category={badgeCategory}
            icon={['far', 'thumbs-up']}
            isGroupedLast={true}
            isRounded={false}
            onClick={showModal}
            text={competency.endorsements.length}
            tooltip={getMessageWithPlaceholders({
              message: messages.competenciesEndorsementsNumberOf,
              placeholders: {
                numberOfEndorsements: competency.endorsements.length
              }
            })}
          />
        ) : null}

        <style jsx>{`
          .button {
            background-color: transparent;
            border: 0;
            cursor: pointer;
            display: inline-flex;
            gap: 1px;
            min-width: 0;
            max-width: 100%;
            outline: 0;
            font-family: inherit;
            padding: 0;
            white-space: nowrap;
          }
        `}</style>
      </button>

      <ProfileCompetenciesDetails
        competency={competency}
        enableAdminLinks={enableAdminLinks}
        onHide={hideModal}
        showEndorsements={showEndorsements}
        showLanguageVersionGrantorTitle={showLanguageVersionGrantorTitle}
        targetUserId={targetUserId}
        updateCompetencies={updateCompetencies}
        visible={modalVisible}
      />
    </>
  );
};

export default ProfileCompetenciesBadge;
